import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import InputNumber from 'components/InputNumber';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import React from 'react';
import { useForm } from 'react-hook-form';
import {
  cancelScheduledWithdrawal,
  getScheduledWithdrawals,
  scheduleWithdrawal,
} from 'services/api.service';
import { maskCurrency } from 'utils/number';

type FormData = {
  amount: number;
  keyPix: string;
  description?: string;
  date: string;
  time: string;
};

const STATUS_LABEL: Record<string, string> = {
  SCHEDULED: 'Agendado',
  PROCESSING: 'Em execução',
  EXECUTED: 'Pago',
  FAILED: 'Falhou',
  CANCELED: 'Cancelado',
};

const STATUS_COLOR: Record<string, string> = {
  SCHEDULED: 'blue',
  PROCESSING: 'purple',
  EXECUTED: 'green',
  FAILED: 'red',
  CANCELED: 'gray',
};

const formatDateTime = (iso: string) => {
  const date = new Date(iso);
  return `${date.toLocaleDateString('pt-BR')} ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

/** Offset local em ±HH:MM, para o horário escolhido viajar sem virar UTC. */
function localOffset(): string {
  const minutes = -new Date().getTimezoneOffset();
  const sign = minutes >= 0 ? '+' : '-';
  const abs = Math.abs(minutes);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`;
}

const todayIso = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};

/**
 * Pagamento agendado: o cliente escolhe a chave Pix, o valor, o dia e a hora, e
 * o sistema paga na data — desde que haja saldo.
 *
 * O agendamento NÃO reserva saldo. Isso está dito na tela porque é a pergunta
 * que o cliente faz: o dinheiro continua disponível até a hora do pagamento, e
 * se faltar saldo o sistema retenta ao longo do dia antes de desistir.
 */
export default function ScheduledWithdrawalsPage() {
  const toast = useToast();
  const { requestTwoFa } = useTwoFaHelper();
  const [loading, setLoading] = React.useState(false);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { date: todayIso(), time: '09:00' } });

  const { data, isLoading, refetch } = useQuery(['scheduled-withdrawals'], () =>
    getScheduledWithdrawals(),
  );

  const submit = async (form: FormData) => {
    const code = await requestTwoFa();
    if (!code?.id) return;

    setLoading(true);
    try {
      await scheduleWithdrawal(code.id, {
        amount: form.amount,
        keyPix: form.keyPix,
        description: form.description,
        type: 'DICT',
        scheduledFor: `${form.date}T${form.time}:00${localOffset()}`,
      });
      toast({ title: 'Pagamento agendado', status: 'success', duration: 3000 });
      reset({ date: todayIso(), time: '09:00' });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível agendar',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setLoading(false);
    }
  };

  const cancel = async (id: string) => {
    if (!window.confirm('Cancelar este agendamento?')) return;
    try {
      await cancelScheduledWithdrawal(id);
      toast({ title: 'Agendamento cancelado', status: 'success', duration: 3000 });
      refetch();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Não foi possível cancelar',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const rows = data ?? [];

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }} px={{ base: 4, md: 0 }}>
      <Heading size="lg" mb={4}>
        Pagamentos agendados
      </Heading>

      <Box bg="white" borderRadius="20px" p={6} boxShadow="sm" mb={6}>
        <Heading size="sm" mb={4}>
          Novo agendamento
        </Heading>

        <VStack as="form" onSubmit={handleSubmit(submit)} align="stretch" spacing={4}>
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
            <InputNumber
              control={control}
              name="amount"
              label="Valor"
              placeholder="R$ 0,00"
              rules={{
                required: 'Informe o valor',
                validate: { positive: (v: number) => v > 0 || 'O valor deve ser maior que 0' },
              }}
            />
            <FormControl isInvalid={!!errors.keyPix}>
              <FormLabel fontSize="sm">Chave Pix do destinatário</FormLabel>
              <Input
                size="sm"
                placeholder="CPF/CNPJ, e-mail, telefone ou chave aleatória"
                {...register('keyPix', { required: true })}
              />
            </FormControl>
            <FormControl isInvalid={!!errors.date}>
              <FormLabel fontSize="sm">Data do pagamento</FormLabel>
              <Input size="sm" type="date" min={todayIso()} {...register('date', { required: true })} />
            </FormControl>
            <FormControl isInvalid={!!errors.time}>
              <FormLabel fontSize="sm">Horário</FormLabel>
              <Input size="sm" type="time" {...register('time', { required: true })} />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Descrição</FormLabel>
              <Input size="sm" placeholder="Opcional" {...register('description')} />
            </FormControl>
          </SimpleGrid>

          <Alert status="info" borderRadius="12px">
            <AlertIcon />
            <Text fontSize="sm">
              O agendamento não reserva saldo: o valor continua disponível até a hora do pagamento.
              Se faltar saldo na hora marcada, o sistema tenta de novo ao longo do dia antes de
              marcar como falha.
            </Text>
          </Alert>

          <Flex justify="flex-end">
            <Button type="submit" colorScheme="green" isLoading={loading}>
              Agendar pagamento
            </Button>
          </Flex>
        </VStack>
      </Box>

      <Box bg="white" borderRadius="20px" p={6} boxShadow="sm">
        <Heading size="sm" mb={4}>
          Agendamentos
        </Heading>

        {isLoading ? (
          <Flex justify="center" py={8}>
            <Spinner />
          </Flex>
        ) : (
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Data e hora</Th>
                <Th isNumeric>Valor</Th>
                <Th>Descrição</Th>
                <Th>Status</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  <Td>{formatDateTime(row.scheduledFor)}</Td>
                  <Td isNumeric>{maskCurrency(Number(row.amount))}</Td>
                  <Td>
                    {row.description ?? '—'}
                    {row.status === 'FAILED' && row.lastError && (
                      <Text fontSize="xs" color="red.500">
                        {row.lastError}
                      </Text>
                    )}
                  </Td>
                  <Td>
                    <Badge colorScheme={STATUS_COLOR[row.status] ?? 'gray'}>
                      {STATUS_LABEL[row.status] ?? row.status}
                    </Badge>
                  </Td>
                  <Td>
                    {row.status === 'SCHEDULED' && (
                      <Button size="xs" variant="ghost" colorScheme="red" onClick={() => cancel(row.id)}>
                        Cancelar
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
              {rows.length === 0 && (
                <Tr>
                  <Td colSpan={5}>
                    <Text fontSize="sm" color="gray.500" textAlign="center" py={4}>
                      Nenhum pagamento agendado.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        )}
      </Box>
    </Box>
  );
}
